import type { ReportLocale } from '../catalogue-contributions/report-copy';

const en = {
  title: 'Reader reports',
  intro:
    'Reports about public reviews, thoughts, quotes and reader profiles. A report is an allegation: judge the content itself. Removed posts stay with their author as private; nobody is told who reported them.',
  refresh: 'Refresh',
  tabs: { queue: 'Queue', log: 'Moderation log', restrictions: 'Restrictions' },
  filters: { all: 'All', content: 'Posts', reader: 'Profiles' },
  totals: '{cases} open cases · {reports} pending reports',
  empty: 'No open reports.',
  loading: 'Loading reports…',
  failed: 'Reports could not be loaded. Refresh and try again.',
  actionFailed: 'The decision could not be saved. Refresh and try again.',
  stale: 'Another moderator already resolved this case.',
  saved: 'Decision saved.',
  loadMore: 'Load more',
  kinds: { review: 'Review', thought: 'Thought', line: 'Quote', reader: 'Reader profile' },
  states: {
    public: 'Public',
    not_public: 'No longer public',
    removed: 'Removed by a moderator',
    deleted: 'Deleted by the author',
  },
  reasons: {
    spam: 'Spam or advertising',
    harassment: 'Harassment or hate',
    sexual_or_violent: 'Sexual or violent content',
    spoilers: 'Spoilers without a warning',
    other: 'Something else',
  },
  pending: '{count} pending',
  previously: '{count} resolved earlier',
  firstReported: 'First reported {date}',
  book: 'Book',
  line: 'On the quote',
  rating: 'Rating {value}',
  spoilers: 'Marked as spoilers',
  reportedVersion: 'Version the reporter saw',
  author: 'Author',
  anonymous: 'Anonymous quote — no author is shown to readers.',
  restrictedUntil: 'Public posting restricted until {date}',
  restrictedPermanent: 'Public posting restricted permanently',
  authorHistory: '{actioned} upheld reports · {removed} removed posts',
  reports: 'Reports',
  reporter: 'Reporter',
  reporterHistory:
    '{total} reports ({recent} in 30 days) · {dismissed} dismissed · {actioned} upheld',
  note: 'Moderator note (optional, internal)',
  duration: 'Restriction length',
  durations: { 7: '7 days', 30: '30 days', 90: '90 days', permanent: 'Permanent' },
  dismiss: 'Dismiss',
  remove: 'Remove post',
  restrict: 'Restrict author',
  removeAndRestrict: 'Remove and restrict',
  hidePhoto: 'Hide profile photo',
  restore: 'Restore',
  lift: 'Lift restriction',
  logEmpty: 'No moderator actions yet.',
  restrictionsEmpty: 'No active restrictions.',
  actions: {
    dismiss: 'Dismissed',
    remove_content: 'Removed post',
    restore_content: 'Restored post',
    restrict_author: 'Restricted author',
    lift_restriction: 'Lifted restriction',
  },
  by: 'by {name}',
  since: 'since {date}',
  until: 'until {date}',
  permanent: 'permanent',
};
type Copy = typeof en;

const sk: Copy = {
  title: 'Nahlásenia čitateľov',
  intro:
    'Nahlásenia verejných recenzií, myšlienok, citátov a profilov čitateľov. Nahlásenie je obvinenie: posúď samotný obsah. Odstránené príspevky zostanú autorovi ako súkromné; nikto sa nedozvie, kto ich nahlásil.',
  refresh: 'Obnoviť',
  tabs: { queue: 'Fronta', log: 'Záznam moderovania', restrictions: 'Obmedzenia' },
  filters: { all: 'Všetko', content: 'Príspevky', reader: 'Profily' },
  totals: '{cases} otvorených prípadov · {reports} čakajúcich nahlásení',
  empty: 'Žiadne otvorené nahlásenia.',
  loading: 'Načítavam nahlásenia…',
  failed: 'Nahlásenia sa nepodarilo načítať. Obnov stránku a skús to znova.',
  actionFailed: 'Rozhodnutie sa nepodarilo uložiť. Obnov stránku a skús to znova.',
  stale: 'Tento prípad už vyriešil iný moderátor.',
  saved: 'Rozhodnutie je uložené.',
  loadMore: 'Načítať ďalšie',
  kinds: { review: 'Recenzia', thought: 'Myšlienka', line: 'Citát', reader: 'Profil čitateľa' },
  states: {
    public: 'Verejné',
    not_public: 'Už nie je verejné',
    removed: 'Odstránil moderátor',
    deleted: 'Autor ho vymazal',
  },
  reasons: {
    spam: 'Spam alebo reklama',
    harassment: 'Obťažovanie alebo nenávisť',
    sexual_or_violent: 'Sexuálny alebo násilný obsah',
    spoilers: 'Spoilery bez upozornenia',
    other: 'Niečo iné',
  },
  pending: '{count} čakajúcich',
  previously: '{count} vyriešených skôr',
  firstReported: 'Prvé nahlásenie {date}',
  book: 'Kniha',
  line: 'K citátu',
  rating: 'Hodnotenie {value}',
  spoilers: 'Označené ako spoiler',
  reportedVersion: 'Verzia, ktorú videl nahlasovateľ',
  author: 'Autor',
  anonymous: 'Anonymný citát — čitateľom sa autor nezobrazuje.',
  restrictedUntil: 'Verejné príspevky obmedzené do {date}',
  restrictedPermanent: 'Verejné príspevky obmedzené natrvalo',
  authorHistory: '{actioned} potvrdených nahlásení · {removed} odstránených príspevkov',
  reports: 'Nahlásenia',
  reporter: 'Nahlasovateľ',
  reporterHistory:
    '{total} nahlásení ({recent} za 30 dní) · {dismissed} zamietnutých · {actioned} potvrdených',
  note: 'Poznámka moderátora (nepovinná, interná)',
  duration: 'Dĺžka obmedzenia',
  durations: { 7: '7 dní', 30: '30 dní', 90: '90 dní', permanent: 'Natrvalo' },
  dismiss: 'Zamietnuť',
  remove: 'Odstrániť príspevok',
  restrict: 'Obmedziť autora',
  removeAndRestrict: 'Odstrániť a obmedziť',
  hidePhoto: 'Skryť profilovú fotku',
  restore: 'Obnoviť',
  lift: 'Zrušiť obmedzenie',
  logEmpty: 'Zatiaľ žiadne zásahy moderátorov.',
  restrictionsEmpty: 'Žiadne aktívne obmedzenia.',
  actions: {
    dismiss: 'Zamietnuté',
    remove_content: 'Odstránený príspevok',
    restore_content: 'Obnovený príspevok',
    restrict_author: 'Obmedzený autor',
    lift_restriction: 'Zrušené obmedzenie',
  },
  by: 'moderátor {name}',
  since: 'od {date}',
  until: 'do {date}',
  permanent: 'natrvalo',
};
const cs: Copy = {
  title: 'Nahlášení čtenářů',
  intro:
    'Nahlášení veřejných recenzí, myšlenek, citátů a profilů čtenářů. Nahlášení je obvinění: posuď samotný obsah. Odstraněné příspěvky zůstanou autorovi jako soukromé; nikdo se nedozví, kdo je nahlásil.',
  refresh: 'Obnovit',
  tabs: { queue: 'Fronta', log: 'Záznam moderování', restrictions: 'Omezení' },
  filters: { all: 'Vše', content: 'Příspěvky', reader: 'Profily' },
  totals: '{cases} otevřených případů · {reports} čekajících nahlášení',
  empty: 'Žádná otevřená nahlášení.',
  loading: 'Načítám nahlášení…',
  failed: 'Nahlášení se nepodařilo načíst. Obnov stránku a zkus to znovu.',
  actionFailed: 'Rozhodnutí se nepodařilo uložit. Obnov stránku a zkus to znovu.',
  stale: 'Tento případ už vyřešil jiný moderátor.',
  saved: 'Rozhodnutí je uloženo.',
  loadMore: 'Načíst další',
  kinds: { review: 'Recenze', thought: 'Myšlenka', line: 'Citát', reader: 'Profil čtenáře' },
  states: {
    public: 'Veřejné',
    not_public: 'Už není veřejné',
    removed: 'Odstranil moderátor',
    deleted: 'Autor ho smazal',
  },
  reasons: {
    spam: 'Spam nebo reklama',
    harassment: 'Obtěžování nebo nenávist',
    sexual_or_violent: 'Sexuální nebo násilný obsah',
    spoilers: 'Spoilery bez upozornění',
    other: 'Něco jiného',
  },
  pending: '{count} čekajících',
  previously: '{count} vyřešených dříve',
  firstReported: 'První nahlášení {date}',
  book: 'Kniha',
  line: 'K citátu',
  rating: 'Hodnocení {value}',
  spoilers: 'Označeno jako spoiler',
  reportedVersion: 'Verze, kterou viděl oznamovatel',
  author: 'Autor',
  anonymous: 'Anonymní citát — čtenářům se autor nezobrazuje.',
  restrictedUntil: 'Veřejné příspěvky omezeny do {date}',
  restrictedPermanent: 'Veřejné příspěvky omezeny natrvalo',
  authorHistory: '{actioned} potvrzených nahlášení · {removed} odstraněných příspěvků',
  reports: 'Nahlášení',
  reporter: 'Oznamovatel',
  reporterHistory:
    '{total} nahlášení ({recent} za 30 dní) · {dismissed} zamítnutých · {actioned} potvrzených',
  note: 'Poznámka moderátora (nepovinná, interní)',
  duration: 'Délka omezení',
  durations: { 7: '7 dní', 30: '30 dní', 90: '90 dní', permanent: 'Natrvalo' },
  dismiss: 'Zamítnout',
  remove: 'Odstranit příspěvek',
  restrict: 'Omezit autora',
  removeAndRestrict: 'Odstranit a omezit',
  hidePhoto: 'Skrýt profilovou fotku',
  restore: 'Obnovit',
  lift: 'Zrušit omezení',
  logEmpty: 'Zatím žádné zásahy moderátorů.',
  restrictionsEmpty: 'Žádná aktivní omezení.',
  actions: {
    dismiss: 'Zamítnuto',
    remove_content: 'Odstraněný příspěvek',
    restore_content: 'Obnovený příspěvek',
    restrict_author: 'Omezený autor',
    lift_restriction: 'Zrušené omezení',
  },
  by: 'moderátor {name}',
  since: 'od {date}',
  until: 'do {date}',
  permanent: 'natrvalo',
};

export const COMMUNITY_REPORT_COPY: Record<ReportLocale, Copy> = { en, sk, cs };

export function fill(template: string, values: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/gu, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}
